const getConnection = () => {
  if (typeof navigator === "undefined") return null;
  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  );
};

export const getCaptecPerformanceProfile = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      lowResource: false,
      reducedMotion: false,
      fastScrollVelocity: 1400,
    };
  }

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const compactViewport = window.matchMedia("(max-width: 900px)").matches;
  const connection = getConnection();
  const memory = Number(navigator.deviceMemory || 0);
  const cores = Number(navigator.hardwareConcurrency || 0);
  const saveData = Boolean(connection?.saveData);
  const slowConnection = ["slow-2g", "2g"].includes(connection?.effectiveType);

  const constrainedMemory = memory > 0 && memory <= 4;
  const constrainedCpu = cores > 0 && cores <= 4;

  const lowResource = Boolean(
    reducedMotion ||
      saveData ||
      slowConnection ||
      constrainedMemory ||
      constrainedCpu ||
      compactViewport
  );

  return {
    lowResource,
    reducedMotion,
    fastScrollVelocity: lowResource ? 700 : 1300,
  };
};

export const isFastScrollTrigger = (trigger, threshold = 1300) => {
  if (!trigger || typeof trigger.getVelocity !== "function") return false;
  return Math.abs(trigger.getVelocity()) >= threshold;
};

export const setCaptecPerformanceClass = (root, profile) => {
  if (!root) return;
  root.classList.toggle("is-low-resource", Boolean(profile?.lowResource));
  root.classList.toggle("is-reduced-motion", Boolean(profile?.reducedMotion));
};

/**
 * Releases a completed one-way ScrollTrigger pin and removes its artificial
 * runway. The section is held fixed for two frames while the exact spacer
 * height is subtracted from scrollY, so the viewport does not jump. Once
 * released, reverse scrolling crosses only the section's natural height.
 */
export const releaseCompletedScrollPin = ({ trigger, section, onReleased }) => {
  if (!trigger || !section || section.dataset.captecPinRelease === "done") {
    return;
  }

  if (section.dataset.captecPinRelease === "scheduled") return;
  section.dataset.captecPinRelease = "scheduled";

  window.requestAnimationFrame(() => {
    if (!section.isConnected || section.dataset.captecPinRelease === "done") {
      return;
    }

    const sectionRect = section.getBoundingClientRect();
    const spacer =
      trigger.pin?.parentElement?.classList?.contains("pin-spacer")
        ? trigger.pin.parentElement
        : null;
    const naturalHeight = Math.max(1, sectionRect.height || section.offsetHeight);
    const spacerHeight = spacer
      ? spacer.getBoundingClientRect().height
      : naturalHeight + Math.max(0, Number(trigger.end) - Number(trigger.start));
    const removedHeight = Math.max(0, spacerHeight - naturalHeight);
    const currentY = window.scrollY || 0;
    const targetY = Math.max(0, currentY - removedHeight);
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    const previousOverflowAnchor = document.body.style.overflowAnchor;

    section.style.setProperty(
      "--captec-pin-release-top",
      `${sectionRect.top.toFixed(2)}px`
    );
    section.style.setProperty(
      "--captec-pin-release-height",
      `${naturalHeight.toFixed(2)}px`
    );
    section.classList.add("is-releasing-scroll-pin");

    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.overflowAnchor = "none";

    trigger.kill(true);

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: targetY, left: 0, behavior: "auto" });

      window.requestAnimationFrame(() => {
        if (!section.isConnected) return;

        section.classList.remove("is-releasing-scroll-pin");
        section.style.removeProperty("--captec-pin-release-top");
        section.style.removeProperty("--captec-pin-release-height");
        section.dataset.captecPinRelease = "done";
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
        document.body.style.overflowAnchor = previousOverflowAnchor;

        window.dispatchEvent(
          new CustomEvent("captec:scroll-pin-released", {
            detail: { removedHeight, targetY },
          })
        );
        onReleased?.({ removedHeight, targetY });
      });
    });
  });
};
